import Copy16 from '@carbon/icons-react/es/copy/16';
import Edit16 from '@carbon/icons-react/es/edit/16';
import Export16 from '@carbon/icons-react/es/export/16';
import React, { ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import {
  CreateCustomCardWidgetPayload,
  CreateCustomChartWidgetPayload,
  CreateWidgetPayload,
  SimpleSearchFilter,
  Widget,
  WidgetType,
} from 'api/dashboard/widget';
import { Menu } from 'components/atoms/dropdown';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useTranslation } from 'hooks/use-translation';
import {
  useCreateWidgetMutation,
  useDeleteWidgetMutation,
} from 'queries/widgets';
import { useGetEditWidgetUrlWithSearch } from 'utils/urls';
import {
  byNumberTypeWidget,
  useDashboardExports,
  useGetChartAndCardWidgetCount,
} from './components/utils';
import { scrollToBottomOfDashboard } from './scroll';

export const useWidgetMenu = (
  widget: Widget,
  dashboardSimpleFilter: SimpleSearchFilter,
): ReactElement => {
  const { dashboardId, widgetId, widgetName, widgetType, simpleSearchFilter } =
    widget;

  const { chartTypeWidgetsCount, numberTypeWidgetsCount } =
    useGetChartAndCardWidgetCount(Number(dashboardId));

  const getEditWidgetUrl = useGetEditWidgetUrlWithSearch();

  const { mutate: createWidget } = useCreateWidgetMutation();
  const { mutateAsync: deleteWidget } = useDeleteWidgetMutation();
  const cardText = useTranslation('dashboard.card');
  const widgetText = useTranslation('dashboard.widget');
  const confirmPopup = useDeleteConfirmModal({
    content: useTranslation('dashboard.widget.delete.message', {
      widgetName: <strong>{widgetName}</strong>,
      widgetType: widgetType === WidgetType.CustomCard ? cardText : widgetText,
    }),
    title: useTranslation('dashboard.widget.delete.title'),
    onOk: () => deleteWidget({ widgetId, dashboardId }),
  });

  const { exportFn } = useDashboardExports(widgetId);

  const history = useHistory();
  const goToEdit = () => {
    history.push(getEditWidgetUrl(dashboardId, widgetId));
  };
  const duplicate = () => {
    const templatePayload = {
      widgetType,
      simpleSearchFilter,
      dashboardId: widget.dashboardId,
      widgetName: `${widget.widgetName} - Clone`,
      widgetOrder: byNumberTypeWidget(widgetType)
        ? numberTypeWidgetsCount
        : chartTypeWidgetsCount,
    };
    let payload: CreateWidgetPayload;

    switch (widgetType) {
      case WidgetType.CustomCard:
        payload = {
          ...templatePayload,
          metaData: (widget as CreateCustomCardWidgetPayload).metaData,
        };
        break;
      case WidgetType.CustomChart:
        payload = {
          ...templatePayload,
          widgetObjectType: (widget as CreateCustomChartWidgetPayload)
            .widgetObjectType,
          metaData: (widget as CreateCustomChartWidgetPayload).metaData,
        };
        break;
      default:
        payload = templatePayload;
        break;
    }

    createWidget(payload, {
      onSuccess: (newWidget) => {
        if (
          ![WidgetType.ProjectCount, WidgetType.CustomCard].includes(
            newWidget.widgetType,
          )
        ) {
          scrollToBottomOfDashboard();
        }
      },
    });
  };

  return (
    <Menu>
      <Menu.Header>
        <FormattedMessage id="more_actions" />
      </Menu.Header>
      <Menu.Item icon={<Edit16 />} onClick={goToEdit}>
        <FormattedMessage id="edit" />
      </Menu.Item>
      <Menu.Item icon={<Copy16 />} onClick={duplicate}>
        <FormattedMessage id="duplicate" />
      </Menu.Item>
      <Menu.Item
        icon={<Export16 />}
        onClick={() =>
          exportFn({
            drillDownFilter: widget.simpleSearchFilter,
            dashboardFilter: dashboardSimpleFilter,
          })
        }
      >
        <FormattedMessage id="export" />
      </Menu.Item>
      <Menu.Divider />
      <Menu.ItemDelete onClick={confirmPopup} />
    </Menu>
  );
};
